import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SettingsIcon from '@mui/icons-material/Settings';
import LinkOffIcon from '@mui/icons-material/LinkOff';

interface AppleAccountUnlinkDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  deviceModel: string;
}

const unlinkSteps = [
  {
    icon: <SettingsIcon />,
    title: '설정 앱 열기 > [Apple ID(상단 프로필)] 탭 > 나의 찾기',
    description: '설정 앱을 열고 상단의 Apple ID 프로필을 탭한 후 "나의 찾기"로 이동하세요'
  },
  {
    icon: <PhoneAndroidIcon />,
    title: '내 기기 목록에서 AirPods 선택',
    description: '기기 목록에서 연동을 해제할 AirPods를 찾아 선택하세요'
  },
  {
    icon: <LinkOffIcon />,
    title: '이 기기 제거 선택',
    description: '기기 정보 화면에서 "이 기기 제거" 버튼을 탭하여 연동을 해제하세요'
  }
];

export default function AppleAccountUnlinkDialog({
  open,
  onClose,
  onConfirm,
  deviceModel
}: AppleAccountUnlinkDialogProps) {
  const [isUnlinkConfirmed, setIsUnlinkConfirmed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleClose = () => {
    setIsUnlinkConfirmed(false);
    onClose();
  };

  const handleConfirm = () => {
    if (isUnlinkConfirmed) {
      onConfirm();
      setIsUnlinkConfirmed(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 6,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: isMobile ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: isMobile 
            ? 'none' 
            : '0 32px 64px rgba(0, 0, 0, 0.12), 0 8px 32px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          height: isMobile ? '100vh' : 'auto',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      {/* 헤더 */}
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%)',
          backdropFilter: 'blur(20px)',
          p: isMobile ? 2 : 4,
          textAlign: 'center',
          borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningAmberIcon sx={{ color: '#6366f1', fontSize: 28 }} />
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={800} sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              애플 계정 연동 해제 필수
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 2 : 4 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* 중요 안내 박스 */}
          <Alert 
            severity="info" 
            icon={<WarningAmberIcon />}
            sx={{ 
              mb: 4,
              borderRadius: 3,
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
              border: '1px solid rgba(99, 102, 241, 0.2)',
              '& .MuiAlert-icon': { color: '#6366f1' }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151', lineHeight: 1.6 }}>
              기기와 애플계정과의 연동이 해제 완료된 기기에 한해 판매가 가능합니다.
            </Typography>
            <Typography variant="body2" sx={{ color: '#ef4444', mt: 1, fontWeight: 600 }}>
              연동이 해제되지 않은 경우 자동 반송 처리될 수 있습니다.
            </Typography>
          </Alert>



          {/* 연동 해제 방법 단계 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#374151' }}>
              📱 애플 계정 연동 해제 방법
            </Typography>
            <List sx={{ p: 0 }}>
              {unlinkSteps.map((step, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      px: 0,
                      py: 2,
                      backgroundColor: index % 2 === 0 ? 'rgba(248, 250, 252, 0.5)' : 'transparent',
                      borderRadius: 2,
                      mb: 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(99, 102, 241, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6366f1',
                          border: '2px solid rgba(99, 102, 241, 0.2)'
                        }}
                      >
                        {React.cloneElement(step.icon, { sx: { fontSize: 20 } })}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>
                          {index + 1}. {step.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.5 }}>
                          {step.description}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < unlinkSteps.length - 1 && (
                    <Divider sx={{ my: 1, borderColor: 'rgba(226, 232, 240, 0.6)' }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>

          {/* 확인 체크박스 */}
          <Box
            sx={{
              p: 3,
              backgroundColor: 'rgba(99, 102, 241, 0.05)',
              borderRadius: 3,
              border: '1px solid rgba(99, 102, 241, 0.2)',
              mb: 2
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isUnlinkConfirmed}
                  onChange={(e) => setIsUnlinkConfirmed(e.target.checked)}
                  sx={{
                    color: '#6366f1',
                    '&.Mui-checked': { color: '#6366f1' },
                    '& .MuiSvgIcon-root': { fontSize: 24 }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleOutlineIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                    애플 계정 연동 해제를 완료했습니다
                  </Typography>
                </Box>
              }
            />
            <Typography variant="body2" sx={{ color: '#6b7280', ml: 5, mt: 0.5 }}>
              위 단계를 모두 완료한 후 체크해주세요.
            </Typography>
          </Box>
        </motion.div>
      </DialogContent>

      {/* 액션 버튼 */}
      <DialogActions
        sx={{
          p: isMobile ? 2 : 4,
          background: 'rgba(248, 250, 252, 0.95)',
          borderTop: '1px solid rgba(226, 232, 240, 0.5)',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            fontWeight: 600,
            px: isMobile ? 2 : 3,
            py: 1.5,
            borderRadius: 3,
            color: '#6b7280',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.08)',
              color: '#374151',
            }
          }}
        >
          취소
        </Button>
        
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!isUnlinkConfirmed}
          sx={{
            fontWeight: 700,
            px: isMobile ? 3 : 4,
            py: 1.5,
            borderRadius: 3,
            background: isUnlinkConfirmed 
              ? 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)' 
              : 'rgba(156, 163, 175, 0.5)',
            boxShadow: isUnlinkConfirmed 
              ? '0 4px 12px rgba(99, 102, 241, 0.3)' 
              : 'none',
            '&:hover': {
              background: isUnlinkConfirmed 
                ? 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)' 
                : 'rgba(156, 163, 175, 0.5)',
              boxShadow: isUnlinkConfirmed 
                ? '0 6px 16px rgba(99, 102, 241, 0.4)' 
                : 'none',
            },
            '&:disabled': {
              background: 'rgba(156, 163, 175, 0.5)',
              color: 'rgba(255, 255, 255, 0.7)',
              boxShadow: 'none',
            }
          }}
        >
          {isUnlinkConfirmed ? '확인 및 기기 추가' : '연동 해제 확인 필요'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}